import { useState, useEffect } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, UploadCloud, Loader2, ChevronRight, Trash2, PoundSterling, Percent, CalendarClock, HandCoins, Boxes, ToggleRight } from 'lucide-react';
import PricingCategoryList from './PricingCategoryList';
import PricingUploadModal from '../PricingUploadModal';
import PricingRebatesTab from './PricingRebatesTab';
import PricingScheduledIncreasesTab from './PricingScheduledIncreasesTab';
import PricingMfSupportTab from './PricingMfSupportTab';
import B29SettingsPanel from './B29SettingsPanel';
import { SupplierActiveToggle } from './PricingActiveToggle';

const enconOrange = '#ee7203';

/**
 * The Suppliers -> Categories -> Products browser, reused as-is for both the
 * "main" catalog and the isolated B29 catalog (routes/pricing.py `scope`
 * field) — same components, just a different `scope` query/body param on
 * every call, per user instruction to reuse rather than duplicate.
 */
const PricingCatalogPanel = ({ scope }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [turnAllOnConfirming, setTurnAllOnConfirming] = useState(false);
  const [turningAllOn, setTurningAllOn] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/pricing/suppliers`, { params: { scope } });
      setSuppliers(res.data?.suppliers || []);
    } catch {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); setSelectedSupplier(null); }, [scope]);

  const turnAllSuppliersOn = async () => {
    setTurningAllOn(true);
    try {
      const res = await api.post(`${API}/pricing/suppliers/set-active-bulk`, { scope, is_active: true });
      toast.success(`Turned ON pricing for all ${res.data?.supplier_count || 0} supplier(s)`);
      setTurnAllOnConfirming(false);
      load();
    } catch {
      toast.error('Failed to turn all suppliers on');
    } finally {
      setTurningAllOn(false);
    }
  };

  const submitNewSupplier = async () => {
    if (!newSupplierName.trim()) return;
    try {
      await api.post(`${API}/pricing/suppliers`, { name: newSupplierName, scope });
      toast.success('Supplier added');
      setNewSupplierName(''); setAddingSupplier(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add supplier');
    }
  };

  const deleteSupplier = async (s) => {
    if (!window.confirm(`Delete "${s.name}" and ALL its categories/products? This cannot be undone.`)) return;
    try {
      await api.delete(`${API}/pricing/suppliers/${s.id}`);
      toast.success('Supplier deleted');
      load();
    } catch {
      toast.error('Failed to delete supplier');
    }
  };

  if (selectedSupplier) {
    return <PricingCategoryList supplier={selectedSupplier} onBack={() => setSelectedSupplier(null)} />;
  }

  return (
    <div data-testid={`pricing-catalog-panel-${scope}`}>
      <div className="flex justify-end gap-2 mb-4">
        {scope === 'b29' && (
          <Button
            variant="outline"
            onClick={() => setTurnAllOnConfirming(true)}
            className="border-green-500 text-green-700 hover:bg-green-50"
            data-testid={`pricing-turn-all-on-btn-${scope}`}
          >
            <ToggleRight className="w-4 h-4 mr-1" /> Turn All Suppliers On
          </Button>
        )}
        <Button variant="outline" onClick={() => setAddingSupplier(true)} data-testid={`pricing-add-supplier-btn-${scope}`}>
          <Plus className="w-4 h-4 mr-1" /> Add Supplier
        </Button>
        <Button onClick={() => setUploadOpen(true)} style={{ backgroundColor: enconOrange }} className="text-white" data-testid={`pricing-upload-btn-${scope}`}>
          <UploadCloud className="w-4 h-4 mr-1" /> Upload Price Sheet
        </Button>
      </div>

      <Dialog open={turnAllOnConfirming} onOpenChange={(v) => { if (!v) setTurnAllOnConfirming(false); }}>
        <DialogContent className="bg-white max-w-sm" data-testid="pricing-turn-all-on-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Turn ON pricing for all {suppliers.length} B29 supplier(s)?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This will turn ON every product group (category) under every B29 supplier. Their prices will
            start showing up in the B29 Stock Transfer builder and Rebate Summary matching.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTurnAllOnConfirming(false)} disabled={turningAllOn}>Cancel</Button>
            <Button
              className="text-white bg-green-600 hover:bg-green-700"
              onClick={turnAllSuppliersOn}
              disabled={turningAllOn}
              data-testid="pricing-turn-all-on-confirm-btn"
            >
              {turningAllOn ? 'Applying...' : 'Yes, turn ON all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {addingSupplier && (
        <div className="mb-4 flex gap-2">
          <Input placeholder="Supplier / manufacturer name" value={newSupplierName} onChange={(e) => setNewSupplierName(e.target.value)} data-testid={`pricing-new-supplier-input-${scope}`} />
          <Button onClick={submitNewSupplier} style={{ backgroundColor: enconOrange }} className="text-white" data-testid={`pricing-new-supplier-submit-${scope}`}>Add</Button>
          <Button variant="outline" onClick={() => setAddingSupplier(false)}>Cancel</Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: enconOrange }} /></div>
      ) : suppliers.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p>No suppliers yet — upload a price sheet or add one manually to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {suppliers.map(s => (
            <Card
              key={s.id}
              className="cursor-pointer hover:shadow-md hover:border-orange-300 transition-all"
              onClick={() => setSelectedSupplier(s)}
              data-testid={`pricing-supplier-card-${s.id}`}
            >
              <CardContent className="p-4 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-800 truncate">{s.name}</p>
                  <p className="text-xs text-gray-400">{s.category_count} categories · {s.product_count} products</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <SupplierActiveToggle
                    supplierId={s.id}
                    supplierName={s.name}
                    activeStatus={s.active_status}
                    activeCount={s.active_category_count}
                    totalCount={s.category_count}
                    onChanged={load}
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSupplier(s); }}
                    className="p-1 text-red-400 hover:bg-red-50 rounded"
                    data-testid={`pricing-delete-supplier-${s.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <PricingUploadModal isOpen={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={load} scope={scope} />
    </div>
  );
};

/**
 * Settings > Pricing tab. Supplier -> Category (product group) -> Product
 * hierarchy for manufacturer price lists. See routes/pricing.py for the data
 * model & rationale (kept separate from the existing Stock Database
 * supplier/product-group system per user instruction).
 */
export const PricingSettings = () => {
  const [activeTab, setActiveTab] = useState('suppliers'); // 'suppliers' | 'rebates' | 'scheduled-increases' | 'mf-support' | 'b29'
  const [suppliers, setSuppliers] = useState([]);

  const loadMainSuppliers = async () => {
    try {
      const res = await api.get(`${API}/pricing/suppliers`, { params: { scope: 'main' } });
      setSuppliers(res.data?.suppliers || []);
    } catch { /* used only for MF Support's supplier picker */ }
  };

  useEffect(() => { loadMainSuppliers(); }, []);

  return (
    <div data-testid="pricing-settings-root">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: enconOrange }}>
            <PoundSterling className="w-5 h-5" /> Pricing
          </h2>
          <p className="text-sm text-gray-500">Supplier price lists — list prices, rebates, discounts and % increases feed straight into Create Quote.</p>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b flex-wrap">
        <button
          onClick={() => setActiveTab('suppliers')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'suppliers' ? '' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          style={activeTab === 'suppliers' ? { borderColor: enconOrange, color: enconOrange } : {}}
          data-testid="pricing-tab-suppliers"
        >
          Suppliers
        </button>
        <button
          onClick={() => setActiveTab('rebates')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'rebates' ? '' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          style={activeTab === 'rebates' ? { borderColor: enconOrange, color: enconOrange } : {}}
          data-testid="pricing-tab-rebates"
        >
          <Percent className="w-3.5 h-3.5" /> Rebates
        </button>
        <button
          onClick={() => setActiveTab('scheduled-increases')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'scheduled-increases' ? '' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          style={activeTab === 'scheduled-increases' ? { borderColor: enconOrange, color: enconOrange } : {}}
          data-testid="pricing-tab-scheduled-increases"
        >
          <CalendarClock className="w-3.5 h-3.5" /> Scheduled Increases
        </button>
        <button
          onClick={() => setActiveTab('mf-support')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'mf-support' ? '' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          style={activeTab === 'mf-support' ? { borderColor: enconOrange, color: enconOrange } : {}}
          data-testid="pricing-tab-mf-support"
        >
          <HandCoins className="w-3.5 h-3.5" /> Manufacturer Price Support
        </button>
        <button
          onClick={() => setActiveTab('b29')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'b29' ? '' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          style={activeTab === 'b29' ? { borderColor: enconOrange, color: enconOrange } : {}}
          data-testid="pricing-tab-b29"
        >
          <Boxes className="w-3.5 h-3.5" /> B29 Pricing
        </button>
      </div>

      {activeTab === 'rebates' ? <PricingRebatesTab />
        : activeTab === 'scheduled-increases' ? <PricingScheduledIncreasesTab />
        : activeTab === 'mf-support' ? <PricingMfSupportTab suppliers={suppliers} onSuppliersChanged={loadMainSuppliers} />
        : activeTab === 'b29' ? (
          <>
            <p className="text-xs text-gray-400 mb-3">
              A completely separate price catalog for B29 central distribution stock — its own suppliers,
              categories and products, isolated from the main Pricing catalog above. Feeds cost prices into
              the B29 Stock Transfer builder.
            </p>
            <B29SettingsPanel />
            <PricingCatalogPanel scope="b29" />
          </>
        ) : <PricingCatalogPanel scope="main" />}
    </div>
  );
};

export default PricingSettings;
